import type { IRatingProcessStepCommitteeUser } from '__generated__';
import { format } from 'date-fns';
import type { BallotLinkRow } from './models';

/** Fallback when the Content-Disposition header is not exposed to the browser (CORS). */
export function buildReportMatrixDocxFileName(committeeFrameworkId: number, date: Date) {
  return `rating-matrix-${committeeFrameworkId}-${format(date, 'yyyyMMdd')}.docx`;
}

export function parseContentDispositionFileName(header: unknown): string | null {
  if (typeof header !== 'string' || !header) {
    return null;
  }

  const encoded = /filename\*\s*=\s*(?:UTF-8|utf-8)''([^;]+)/.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, ''));
    } catch {
      // Fall through to the plain filename.
    }
  }

  const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/.exec(header);
  const value = (plain?.[2] ?? plain?.[1])?.trim();
  return value || null;
}

/** F-04 — a vote takes one of the 20 long-term steps Aaa.il … Ca.il (MidroogRating rank 0–19). */
const BALLOT_VOTE_RANK_MIN = 0;
const BALLOT_VOTE_RANK_MAX = 19;

/** F-04 — anything else is refused with `vote-rating-invalid`. */
export function isBallotVoteRating(rank: number | null | undefined): boolean {
  return rank != null && rank >= BALLOT_VOTE_RANK_MIN && rank <= BALLOT_VOTE_RANK_MAX;
}

/** B-01 — committee positions carry no chair flag; the chair is the "יו"ר וועדה" position. */
const CHAIR_POSITION_TITLE = 'יו"ר וועדה';

export function isChairPosition(title: string | null | undefined): boolean {
  return (title ?? '').trim() === CHAIR_POSITION_TITLE;
}

/** IConflictOfIntesestOption.Value1 = a declared conflict of interest. */
const CONFLICT_OF_INTEREST_YES = 1;

/**
 * B-01 — allowed to vote, voting position, no conflict of interest. Display only; the backend
 * enforces it (voter-position-not-voting / voter-conflict-of-interest).
 */
export function isEligibleBallotVoter(
  row: Pick<IRatingProcessStepCommitteeUser, 'isAllowedToVote' | 'isConflictOfInterests'>,
  positionIsVoting: boolean,
): boolean {
  return (
    row.isAllowedToVote &&
    positionIsVoting &&
    Number(row.isConflictOfInterests) !== CONFLICT_OF_INTEREST_YES
  );
}

export function sameUserId(a: string | null | undefined, b: string | null | undefined): boolean {
  return Boolean(a && b && a.toLowerCase() === b.toLowerCase());
}

/**
 * F-17 — the link the ballot opens on: the primary, else (no ballot data yet) the first chip
 * with a version. Null when no matrix is chosen.
 */
export function pickBallotLink(links: BallotLinkRow[]): BallotLinkRow | null {
  return (
    links.find((link) => link.isPrimary) ??
    links.find((link) => link.ratingMatrixFrameworkTemplateID != null) ??
    null
  );
}
